// components/common/Modal/StoreSelectDialog.tsx
//
// Pick-a-store dialog used by both the tournament reassignment flow
// and the series store reassignment flow. Loads the list of store
// accounts the same way TournamentSetupPage does (users with
// accountType === 'store'), so the option names match what admins
// already see at create time.

import { ReactElement, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { fetchStoreAccounts } from '../../../services/staffService';
import { Button } from '../Button';

export interface StoreOption {
  uid: string;
  name: string;
  storeId?: string;
}

interface StoreSelectDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current store uid, '' or null means "no store". */
  currentStoreUid: string | null;
  title?: string;
  description?: string;
  submitLabel?: string;
  onSubmit: (store: StoreOption | null) => Promise<void> | void;
}

export function StoreSelectDialog({
  open,
  onClose,
  currentStoreUid,
  title = 'Assign to store',
  description,
  submitLabel = 'Save',
  onSubmit,
}: StoreSelectDialogProps): ReactElement {
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedUid(currentStoreUid ?? '');
    setLoadingStores(true);
    (async () => {
      try {
        // Store accounts only via indexed query — not the full /users tree.
        const data = await fetchStoreAccounts();
        const next: StoreOption[] = [];
        for (const [uid, u] of Object.entries(data)) {
          if (u.accountType !== 'store') continue;
          next.push({
            uid,
            name:
              (u.storeName as string) ||
              (u.firstName as string) ||
              (u.email as string) ||
              uid,
            storeId: u.storeId as string | undefined,
          });
        }
        next.sort((a, b) => a.name.localeCompare(b.name));
        setStores(next);
      } finally {
        setLoadingStores(false);
      }
    })();
  }, [open, currentStoreUid]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const picked = selectedUid ? stores.find((s) => s.uid === selectedUid) ?? null : null;
      await onSubmit(picked);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { backgroundColor: '#1E2530', color: '#FFF', minWidth: 420 } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography sx={{ color: '#8892A0', fontSize: '0.85rem', mb: 2 }}>
            {description}
          </Typography>
        )}
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="store-select-label">Store</InputLabel>
          <Select
            labelId="store-select-label"
            label="Store"
            value={selectedUid}
            onChange={(e) => setSelectedUid(e.target.value as string)}
            disabled={loadingStores}
          >
            <MenuItem value="">
              <em>Unassigned (admin / cross-store)</em>
            </MenuItem>
            {stores.map((s) => (
              <MenuItem key={s.uid} value={s.uid}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingStores && (
          <Typography sx={{ color: '#8892A0', fontSize: '0.75rem', mt: 1 }}>
            Loading stores…
          </Typography>
        )}
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(0, 212, 255, 0.08)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
          <Typography sx={{ color: '#00D4FF', fontSize: '0.78rem' }}>
            Changing this transfers ownership immediately. Anyone with permissions on the new store inherits access; the previous owner loses it (unless they&rsquo;re an admin).
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#8892A0' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || loadingStores}
          sx={{ backgroundColor: '#00FFD4', color: '#000', '&:hover': { backgroundColor: '#00E6BE' } }}
        >
          {submitting ? 'Saving…' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StoreSelectDialog;
